import React from 'react';
import {
  Bold,
  Italic,
  Underline,
  Link as LinkIcon,
  AtSign,
  Image,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Upload,
  FileText,
  CalendarClock,
  Paperclip,
} from 'lucide-react';
import { NotesMentionPicker } from '../../../../components/notes/NotesMentionPicker';
import { NOTE_MENTION_CHIP_CLASS, type MentionMember } from '../../../../utils/notesMentions';
import {
  ProjectActivityFormFields,
  type ProjectActivityFormState,
} from '../../../../components/activities/ProjectActivityFormFields';
import type { UserMultiSelectOption } from '../../../../components/UserMultiSelect';
import type { EmployeeMultiSelectOption } from '../../../../components/employees/EmployeeMultiSelect';
import {
  DocumentUploadProgressOverlay,
  type DocumentUploadProgress,
} from '../../../projects/components/DocumentUploadProgress';
import { MAX_LEAD_DOCUMENT_UPLOAD_BYTES } from '../../../projects/utils/leadDocumentFiles';
import { ActivityFormWithSideCalendar } from '../../../../components/activities/activityDayCalendar';
import { LEAD_DETAIL_BTN_OUTLINE, LEAD_DETAIL_BTN_PRIMARY } from './leadDetailStyles';
import { insertListInNotes, noteHtmlHasContent, NotesToolbarButton } from './notesEditorHelpers';
import { NoteLinkModal } from './NoteLinkModal';

type ActivityTypeOption = { id: string; label: string; icon: string; default_checklist_id?: string | null };

/** Gesamtheitliche Box: Tabs (Notizen/Aktivität/Dateien) + Notizbereich + Speichern. */
export function LeadNoteComposer({
  historyContentTab,
  onHistoryContentTabChange,
  notesEditorRef,
  handleNotesEditorInput,
  handleNotesEditorKeyDown,
  handleNotesEditorMentionClick,
  notesSavedRangeRef,
  mentionPickerOpen,
  mentionAnchorRect,
  instanceMembers,
  mentionQuery,
  handleMentionSelect,
  closeMentionPicker,
  linkSavedRangeRef,
  showLinkModal,
  setShowLinkModal,
  linkModalText,
  setLinkModalText,
  linkModalUrl,
  setLinkModalUrl,
  showLinkHint,
  setShowLinkHint,
  openMentionPickerAtCaret,
  imageSavedRangeRef,
  notesImageInputRef,
  noteDraft,
  setNoteDraft,
  saveNotes,
  isSaving,
  notesSaved,
  activityForm,
  setActivityForm,
  activityTypesList,
  activityChecklistOptions,
  activityUserSelectOptions,
  activityEmployeeSelectOptions,
  activityAddressFieldEl,
  activityDayCalendarEl,
  resetActivityForm,
  addActivity,
  addingActivity,
  activitySaveError,
  historyFileInputRef,
  handleUploadDocument,
  documentsUploadError,
  handleDrop,
  handleDragOver,
  handleDragLeave,
  documentsUploading,
  isDragging,
  documentsUploadProgress,
}: {
  historyContentTab: 'notes' | 'activity' | 'files';
  onHistoryContentTabChange: (tab: 'notes' | 'activity' | 'files') => void;
  notesEditorRef: React.RefObject<HTMLDivElement | null>;
  handleNotesEditorInput: () => void;
  handleNotesEditorKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  handleNotesEditorMentionClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  notesSavedRangeRef: React.MutableRefObject<Range | null>;
  mentionPickerOpen: boolean;
  mentionAnchorRect: DOMRect | null;
  instanceMembers: MentionMember[];
  mentionQuery: string;
  handleMentionSelect: (member: MentionMember) => void;
  closeMentionPicker: () => void;
  linkSavedRangeRef: React.MutableRefObject<Range | null>;
  showLinkModal: boolean;
  setShowLinkModal: React.Dispatch<React.SetStateAction<boolean>>;
  linkModalText: string;
  setLinkModalText: React.Dispatch<React.SetStateAction<string>>;
  linkModalUrl: string;
  setLinkModalUrl: React.Dispatch<React.SetStateAction<string>>;
  showLinkHint: boolean;
  setShowLinkHint: React.Dispatch<React.SetStateAction<boolean>>;
  openMentionPickerAtCaret: () => void;
  imageSavedRangeRef: React.MutableRefObject<Range | null>;
  notesImageInputRef: React.RefObject<HTMLInputElement | null>;
  noteDraft: string;
  setNoteDraft: React.Dispatch<React.SetStateAction<string>>;
  saveNotes: (content?: string) => Promise<void> | void;
  isSaving: boolean;
  notesSaved: boolean;
  activityForm: ProjectActivityFormState;
  setActivityForm: React.Dispatch<React.SetStateAction<ProjectActivityFormState>>;
  activityTypesList: ActivityTypeOption[];
  activityChecklistOptions: { value: string; label: string }[];
  activityUserSelectOptions: UserMultiSelectOption[];
  activityEmployeeSelectOptions: EmployeeMultiSelectOption[];
  activityAddressFieldEl: React.ReactNode;
  activityDayCalendarEl: React.ReactNode;
  resetActivityForm: () => void;
  addActivity: () => Promise<boolean> | void;
  addingActivity: boolean;
  activitySaveError?: string | null;
  historyFileInputRef: React.RefObject<HTMLInputElement | null>;
  handleUploadDocument: (e: React.ChangeEvent<HTMLInputElement>) => void;
  documentsUploadError: string | null;
  handleDrop: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  documentsUploading: boolean;
  isDragging: boolean;
  documentsUploadProgress: DocumentUploadProgress | null;
}) {
  return (
    <div className="hidden sm:block rounded-lg border border-app-border bg-app-surface overflow-hidden shrink-0">
      <div className="flex border-b border-app-border overflow-x-auto">
        {[
          { id: 'notes' as const, label: 'Notizen', icon: FileText },
          { id: 'activity' as const, label: 'Aktivität', icon: CalendarClock },
          { id: 'files' as const, label: 'Dateien', icon: Paperclip },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => onHistoryContentTabChange(id)}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
              historyContentTab === id
                ? 'text-pipedrive-blue border-pipedrive-blue bg-blue-50/50'
                : 'text-app-text-muted border-transparent hover:bg-app-hover'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>
      <div
        className={`px-3 pt-3 sm:px-4 sm:pt-4 ${
          historyContentTab === 'notes' ? 'pb-0' : 'pb-3 sm:pb-4'
        }`}
      >
        {historyContentTab === 'notes' && (
          <>
            <div
              ref={notesEditorRef}
              contentEditable
              suppressContentEditableWarning
              className={`min-h-[120px] w-full px-0 py-2 text-sm text-app-text focus:outline-none whitespace-pre-wrap [&_ul]:text-app-text [&_ol]:text-app-text [&_li]:text-app-text [&_li::marker]:text-app-text [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-6 [&_ol]:pl-6 [&_li]:list-outside empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 empty:before:pointer-events-none ${NOTE_MENTION_CHIP_CLASS}`}
              data-placeholder="Hier Beschreibung eingeben …"
              onInput={handleNotesEditorInput}
              onKeyDown={handleNotesEditorKeyDown}
              onMouseDown={handleNotesEditorMentionClick}
              onMouseUp={() => {
                const sel = window.getSelection();
                if (sel?.rangeCount && notesEditorRef.current?.contains(sel.anchorNode)) {
                  notesSavedRangeRef.current = sel.getRangeAt(0).cloneRange();
                }
              }}
              onKeyUp={() => {
                const sel = window.getSelection();
                if (sel?.rangeCount && notesEditorRef.current?.contains(sel.anchorNode)) {
                  notesSavedRangeRef.current = sel.getRangeAt(0).cloneRange();
                }
              }}
              onPaste={(e) => {
                e.preventDefault();
                const text = e.clipboardData.getData('text/plain');
                document.execCommand('insertText', false, text);
              }}
            />
            <NotesMentionPicker
              open={mentionPickerOpen}
              anchorRect={mentionAnchorRect}
              members={instanceMembers}
              query={mentionQuery}
              onSelect={handleMentionSelect}
              onClose={closeMentionPicker}
            />
            <div className="flex items-center justify-between gap-2 mt-2 -mx-3 sm:-mx-4 px-3 sm:px-4 py-2 border-t border-app-border">
              <div className="flex items-center gap-0.5 flex-wrap">
                <NotesToolbarButton editorRef={notesEditorRef} savedRangeRef={notesSavedRangeRef} onCommand="bold" title="Fett" icon={Bold} />
                <NotesToolbarButton editorRef={notesEditorRef} savedRangeRef={notesSavedRangeRef} onCommand="italic" title="Kursiv" icon={Italic} />
                <NotesToolbarButton editorRef={notesEditorRef} savedRangeRef={notesSavedRangeRef} onCommand="underline" title="Unterstrichen" icon={Underline} />
                <span className="w-px h-5 bg-slate-200 mx-1" />
                <span className="relative inline-block">
                  <NotesToolbarButton
                    title="Link einfügen"
                    icon={LinkIcon}
                    onClick={() => {
                      const sel = window.getSelection();
                      const range = sel?.rangeCount ? sel.getRangeAt(0) : null;
                      const text = range?.toString().trim() ?? '';
                      if (!text) {
                        setShowLinkHint(true);
                        return;
                      }
                      linkSavedRangeRef.current = range?.cloneRange() ?? null;
                      setLinkModalText(text);
                      setLinkModalUrl('');
                      setShowLinkModal(true);
                    }}
                  />
                  {showLinkHint && (
                    <div className="absolute left-0 bottom-full mb-1 px-2 py-1.5 text-xs text-white bg-slate-700 rounded shadow-lg whitespace-nowrap z-50">
                      Bitte zuerst Text markieren, der verlinkt werden soll.
                    </div>
                  )}
                </span>
                <NotesToolbarButton icon={AtSign} title="Erwähnung" onClick={openMentionPickerAtCaret} />
                <NotesToolbarButton
                  editorRef={notesEditorRef}
                  savedRangeRef={imageSavedRangeRef}
                  icon={Image}
                  title="Bild einfügen"
                  onClick={() => notesImageInputRef.current?.click()}
                />
                <input
                  ref={notesImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file || !file.type.startsWith('image/')) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      const dataUrl = reader.result as string;
                      notesEditorRef.current?.focus();
                      const sel = window.getSelection();
                      if (sel && imageSavedRangeRef.current) {
                        try {
                          sel.removeAllRanges();
                          sel.addRange(imageSavedRangeRef.current);
                        } catch (_) {}
                      }
                      document.execCommand('insertHTML', false, `<img src="${dataUrl}" alt="Bild" style="max-width:100%;height:auto;" />`);
                    };
                    reader.readAsDataURL(file);
                  }}
                />
                <span className="w-px h-5 bg-slate-200 mx-1" />
                <NotesToolbarButton
                  editorRef={notesEditorRef}
                  savedRangeRef={notesSavedRangeRef}
                  title="Aufzählung"
                  icon={List}
                  onClick={() => insertListInNotes(notesEditorRef, notesSavedRangeRef, false)}
                />
                <NotesToolbarButton
                  editorRef={notesEditorRef}
                  savedRangeRef={notesSavedRangeRef}
                  title="Nummerierte Liste"
                  icon={ListOrdered}
                  onClick={() => insertListInNotes(notesEditorRef, notesSavedRangeRef, true)}
                />
                <span className="w-px h-5 bg-slate-200 mx-1" />
                <NotesToolbarButton editorRef={notesEditorRef} savedRangeRef={notesSavedRangeRef} onCommand="justifyLeft" title="Linksbündig" icon={AlignLeft} />
                <NotesToolbarButton editorRef={notesEditorRef} savedRangeRef={notesSavedRangeRef} onCommand="justifyCenter" title="Zentriert" icon={AlignCenter} />
                <NotesToolbarButton editorRef={notesEditorRef} savedRangeRef={notesSavedRangeRef} onCommand="justifyRight" title="Rechtsbündig" icon={AlignRight} />
              </div>
              <div className="flex items-center gap-2">
                {notesSaved && <span className="text-xs text-emerald-600">Gespeichert.</span>}
                <button
                  type="button"
                  onClick={() => {
                    setNoteDraft('');
                    if (notesEditorRef.current) notesEditorRef.current.innerHTML = '';
                  }}
                  className={LEAD_DETAIL_BTN_OUTLINE}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={() => saveNotes(notesEditorRef.current?.innerHTML ?? '')}
                  disabled={isSaving || !noteHtmlHasContent(noteDraft)}
                  className={LEAD_DETAIL_BTN_PRIMARY}
                >
                  {isSaving ? 'Speichert …' : 'Speichern'}
                </button>
              </div>
            </div>
            <NoteLinkModal
              open={showLinkModal}
              text={linkModalText}
              url={linkModalUrl}
              onTextChange={setLinkModalText}
              onUrlChange={setLinkModalUrl}
              onClose={() => setShowLinkModal(false)}
              onSave={() => {
                const url = linkModalUrl.trim();
                if (!url) return;
                const text = linkModalText.trim();
                notesEditorRef.current?.focus();
                const sel = window.getSelection();
                if (sel && linkSavedRangeRef.current) {
                  sel.removeAllRanges();
                  sel.addRange(linkSavedRangeRef.current);
                  const range = linkSavedRangeRef.current;
                  if (text && text !== range.toString()) {
                    range.deleteContents();
                    const textNode = document.createTextNode(text);
                    range.insertNode(textNode);
                    const next = document.createRange();
                    next.selectNodeContents(textNode);
                    sel.removeAllRanges();
                    sel.addRange(next);
                    linkSavedRangeRef.current = next.cloneRange();
                  }
                  document.execCommand('createLink', false, url);
                }
                setShowLinkModal(false);
              }}
            />
          </>
        )}
        {historyContentTab === 'activity' && (
          <ActivityFormWithSideCalendar
            formColumn={
              // Breathing room between the form and the day calendar on the right.
              <div className="md:pr-6">
              <ProjectActivityFormFields
                form={activityForm}
                setForm={setActivityForm}
                activityTypesList={activityTypesList}
                checklistOptions={activityChecklistOptions}
                userSelectOptions={activityUserSelectOptions}
                employeeSelectOptions={activityEmployeeSelectOptions}
                addressField={activityAddressFieldEl}
              />
              {activitySaveError ? (
                <p className="pt-2 text-sm text-red-600">{activitySaveError}</p>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={resetActivityForm} className={LEAD_DETAIL_BTN_OUTLINE}>
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={() => void addActivity()}
                  disabled={addingActivity || !activityForm.title.trim()}
                  className={LEAD_DETAIL_BTN_PRIMARY}
                >
                  {addingActivity ? 'Speichert …' : 'Speichern'}
                </button>
              </div>
              </div>
            }
            calendar={activityDayCalendarEl}
          />
        )}
        {historyContentTab === 'files' && (
          <div className="space-y-3">
            <input
              ref={historyFileInputRef}
              type="file"
              className="hidden"
              accept="*/*"
              multiple
              onChange={handleUploadDocument}
            />
            {documentsUploadError ? (
              <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {documentsUploadError}
              </p>
            ) : null}
            <div
              data-file-drop-zone
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => !documentsUploading && historyFileInputRef.current?.click()}
              className={`relative flex min-h-[132px] flex-col items-center rounded-lg border-2 border-dashed px-4 py-3 transition-colors select-none ${
                documentsUploading ? 'cursor-default' : 'cursor-pointer'
              } ${
                isDragging
                  ? 'border-sky-500 bg-sky-50/80'
                  : documentsUploading
                    ? 'border-sky-400 bg-sky-50/60'
                    : 'border-sky-200 bg-app-surface-muted/50 hover:border-sky-300 hover:bg-sky-50/50'
              }`}
            >
              {documentsUploading ? (
                <DocumentUploadProgressOverlay progress={documentsUploadProgress} compact />
              ) : null}
              <div
                className={`mt-auto flex flex-col items-center gap-2 text-center w-full ${
                  documentsUploading ? 'invisible' : ''
                }`}
              >
                <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center text-sky-500">
                  <Upload className="w-5 h-5" />
                </div>
                <p className="text-app-text-muted text-sm">
                  Dateien hier ablegen oder{' '}
                  <span className="text-sky-600 underline font-medium">Dateien wählen</span>
                </p>
              </div>
              <div
                className={`mt-3 flex w-full flex-wrap justify-between gap-x-4 gap-y-1 text-[11px] text-app-text-subtle ${
                  documentsUploading ? 'invisible' : ''
                }`}
              >
                <span>PDF, DOC, XLS, Bilder, alle Formate</span>
                <span>Max. {Math.round(MAX_LEAD_DOCUMENT_UPLOAD_BYTES / (1024 * 1024))} MB</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
